import React, { useEffect, useState } from "react";
import styles from "./index.module.scss";

// libraries
import { Formik } from "formik";
import Swal from "sweetalert2";
import OtpInput from "react-otp-input";

// components
import MainHeader from "../../components/Header/MainHeader";
import ContactMessage from "../../components/Messages/ContactMessage";
import DataSafetyMessage from "../../components/Messages/DataSafetyMessage";

// methods
import useResponse from "../../hooks/useResponse";
import ServicesAPI from "../../services/services";
import { enterAadhaarValidation } from "../../validations/user.validation";
import AadhaarExistModal from "../../components/Modal/AadhaarExistModal";
import { googleEvent_LOJ026, googleEvent_LOJ027, googleEvent_LOJ029, googleEvent_LOJ030, googleEvent_LOJ031, googleEvent_LOJ032, googleEvent_LOJ033, googleEvent_LOJ034, googleEvent_LOJ035, googleEvent_LOJ036, googleEvent_LOJ038, googleEvent_LOJ039, googleEvent_LOJ211 } from "../../lib/google-analytics";
import { pixelEvents } from "../../lib/pixel-events";
import CustomButton from "../../components/Button/CustomButton";
import { googleEvent_LOJ024 } from "../../lib/google-analytics-loan-journey-events";
import { useDispatch, useSelector } from "react-redux";
import { setStepperDisable } from "../../redux/slices/userSlice";
import EnterFather from "./components/EnterFather";
import { useLocation, useNavigate } from "react-router-dom";
import { clevertap_Re_kyc_failed, clevertap_Re_kyc_screen, clevertap_Re_kyc_screen_click, clevertap_Re_kyc_success } from "../../lib/clevertap-events";
import EMIsAPI from "../../services/emi-api";
import { handlehideContextualLoader, handleShowContextualLoader } from "../../utils/contextualLoaderTrigger";

export default function ReKYC() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const [isAadharDown, setAadharDown] = useState(false);
  const [openAadhaarModal, setOpenAadhaarModal] = useState({
    bottom: false,
    mobileNo: "",
  });
  const [selectOption, setSelectOption] = useState("");
  const [isOTPVisible, setOTPVisible] = useState(false);
  const [otp, setOtp] = useState("");
  const [seconds, setSeconds] = useState(null);

  const [kycFeatures, setKycFeatures] = useState([]);

  const [fatherName, setFatherName] = useState("");

  const dispatch = useDispatch();
  const location = useLocation();

  const navigate = useNavigate();

  const { handleStep } = useResponse();

  const { accountId } = useSelector((state) => state.user);

  const { selectedLoan } = useSelector((state) => state.register);

  const { checkAadhaarStatusAPI, generateAadhaarOTP, aadhaarVerificationOTP, aadhaarPANVerify, aadhaarVerificationInitiateDigilocker, addFatherNameAPI } = new ServicesAPI();

  useEffect(() => {
    const fetchAPI = async () => {
      try {
        const { paydayEMILoanListAPI } = new EMIsAPI();
        const res = await paydayEMILoanListAPI();

        if (res?.status === "1") {
          setData(res);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAPI();
  }, []);

  useEffect(() => {
    const fetchAadhaarStatus = async () => {
      handleShowContextualLoader();

      try {
        dispatch(setStepperDisable(true));

        const res = await new ServicesAPI().getKycFeaturesAPI();

        if (res?.statusCode === 200) {
          const features = res?.data?.kycFeature ?? [];
          setKycFeatures(features);

          const defaultOption = features.find((feature) => !feature.disabled);
          if (defaultOption) {
            setSelectOption(defaultOption.key);
          }
        }

        await clevertap_Re_kyc_screen({
          amount: selectedLoan?.AmountDetails?.Amount,
        });
      } catch (error) {
        console.error(error);
      } finally {
        handlehideContextualLoader();
      }
    };

    fetchAadhaarStatus();
  }, []);

  useEffect(() => {
    if (seconds > 0) {
      const interval = setInterval(() => {
        setSeconds((prevSecond) => prevSecond - 1);
      }, 1000);

      convertSecondsToMinutes();

      return () => clearInterval(interval);
    }
  }, [seconds]);

  const handleResendAadhaarOTP = async (aadhaar) => {
    const res = await generateAadhaarOTP(aadhaar, accountId);

    if (res?.statusCode === 200) {
      setSeconds(600);
    } else {
      Swal.fire({
        icon: "error",
        text: res.message,
      });
    }

    googleEvent_LOJ034();
  };

  const handleDigilockerAPI = async () => {
    const res = await aadhaarVerificationInitiateDigilocker(accountId);

    if (res && res?.statusCode === 200) {
      localStorage.setItem("reKyc", "1");

      if (window.parent) {
        window.parent.location.href = res.data.url;
      } else {
        window.location.href = res.data.url;
      }
    }

    await clevertap_Re_kyc_screen_click({
      option: "digi_locker",
      amount: selectedLoan?.AmountDetails?.Amount,
    });
  };

  function convertSecondsToMinutes() {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes} minute ${remainingSeconds}`;
  }

  const submitFatherName = async () => {
    try {
      await addFatherNameAPI(fatherName);
    } catch (error) {
      console.error("Add father name error: ", error);
    }
  };

  return (
    <div className="wrapper auto-height">
      <Formik
        initialValues={{ aadhaar: "" }}
        validationSchema={selectOption === "aadhaar" ? enterAadhaarValidation : null}
        onSubmit={(values, { setSubmitting }) => {
          googleEvent_LOJ029(selectOption === "aadhaar" ? "aadhar" : "digilocker");

          setSubmitting(true);

          setTimeout(async () => {
            if (!data?.father_name) {
              await submitFatherName();
            }

            if (selectOption === "aadhaar") {
              await clevertap_Re_kyc_screen_click({
                option: "aadhaar",
                amount: selectedLoan?.AmountDetails?.Amount,
              });

              if (!isOTPVisible) {
                const res = await generateAadhaarOTP(values.aadhaar, accountId);

                if (res && res.statusCode === 200) {
                  setOTPVisible(true);
                  setSeconds(600);
                } else if (res.statusCode === 400) {
                  const data = res?.data?.mobileNo;
                  if (data) {
                    setOpenAadhaarModal({
                      ...openAadhaarModal,
                      bottom: true,
                      mobileNo: data,
                    });
                  } else {
                    Swal.fire({
                      icon: "error",
                      text: res.message,
                      timer: 3000,
                    });
                  }
                } else {
                  Swal.fire({
                    icon: "error",
                    text: res?.message,
                    timer: 3000,
                  });
                }
              } else {
                if (otp.length === 6) {
                  const res = await aadhaarVerificationOTP(values.aadhaar, otp);

                  if (res) {
                    const res = await aadhaarPANVerify(accountId, values.aadhaar);

                    if (res && res.statusCode === 200) {
                      Swal.fire({
                        position: "top-center",
                        icon: "success",
                        title: "Verified Successfully",
                        showConfirmButton: false,
                        allowOutsideClick: false,
                        timer: 2000,
                      });

                      await clevertap_Re_kyc_success({
                        option: "aadhaar",
                      });

                      setTimeout(() => {
                        dispatch(setStepperDisable(false));

                        localStorage.setItem("reKyc", "1");

                        navigate("/loan-approval");

                        // handleStep();
                      }, 2000);

                      // Pixel Events
                      pixelEvents.kyc();
                    } else if (res.statusCode === 400) {
                      Swal.fire({
                        icon: "error",
                        text: res.message,
                        showConfirmButton: false,
                        allowOutsideClick: false,
                        timer: 3000,
                      });
                    }
                  } else {
                    await clevertap_Re_kyc_failed({
                      option: "aadhaar",
                    });
                  }
                } else {
                  Swal.fire({
                    icon: "warning",
                    text: "Please enter aadhaar otp",
                  });
                }
              }
            } else if (selectOption === "digilocker") {
              handleDigilockerAPI();
            } else {
              navigate("/ckyc-verification");
            }

            setSubmitting(false);
          }, 500);
        }}
      >
        {({ errors, values, touched, isSubmitting, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
          <form onSubmit={handleSubmit} style={{ height: "100%" }}>
            <div className={`vertical-column-layout ${styles.aadhaar__styles}`}>
              <div className="vertical-column-top">
                <div className={styles.aadhaar_title}>
                  <h2 className="label mb-1">Re-KYC Required</h2>
                  <p>As per regulatory requirements, your KYC information needs to be updated.</p>
                </div>

                {!data?.father_name && <EnterFather fatherName={fatherName} setFatherName={setFatherName} />}

                <div className={`${styles.kycOptions}`}>
                  {kycFeatures.map((item) => {
                    return (
                      <React.Fragment key={item.key}>
                        {item.key !== "aadhaar" && (
                          <div
                            className={`${styles.selectOption} ${selectOption == item.key ? styles.selected : ""}`}
                            onClick={() => {
                              setSelectOption(item.key);
                            }}
                          >
                            {item.faster && (
                              <div className={`${styles.faster}`}>
                                <img src="../images/fast.png" />
                                <span>Faster</span>
                              </div>
                            )}

                            <div className={styles.selectDigilocker}>
                              <div className={styles.verifyImage}>
                                <img src={item.key === "ckyc" ? "/images/ckyc.png" : "/images/digiloker.svg"} width="40" alt="" />
                              </div>
                              <div className={styles.textBlock}>
                                <div className={styles.name}>{item.name}</div>
                                <div className={styles.subtitle}>{item.subtitle}</div>
                              </div>
                            </div>
                            {item.tag && <div className={styles.recommended}>{item.tag}</div>}
                          </div>
                        )}

                        {item.key === "aadhaar" && (
                          <div
                            className={`${styles.selectOption} ${selectOption == "aadhaar" ? styles.selected : ""}`}
                            onClick={() => {
                              setSelectOption("aadhaar");
                              googleEvent_LOJ026("aadhar");
                            }}
                          >
                            {item.faster && (
                              <div className={`${styles.faster}`}>
                                <img src="../images/fast.png" />
                                <span>Faster</span>
                              </div>
                            )}

                            <div className={styles.selectDigilocker}>
                              <div className={styles.verifyImage}>
                                <img src="/images/adharcard.png" width="50" alt="" />
                              </div>
                              <div className={`${styles.text} ${styles.bigText}`}>Via Aadhar OTP</div>
                            </div>

                            <div className="form-group mt-1 mb-0">
                              <input
                                type="tel"
                                className="form-control"
                                placeholder="Enter your aadhaar number"
                                name="aadhaar"
                                autoComplete="off"
                                value={values.aadhaar}
                                onChange={(e) => {
                                  const input = e.target.value;
                                  if (/^\d*$/.test(input)) {
                                    setFieldValue("aadhaar", input);

                                    if (input.length === 12 && input !== "000000000000") {
                                      googleEvent_LOJ030();
                                    } else if (input.length === 0) {
                                      googleEvent_LOJ031();
                                    }
                                  }
                                }}
                                onBlur={(e) => {
                                  setFieldValue("aadhaar", e.target.value.trim());
                                }}
                                maxLength={12}
                                disabled={isOTPVisible ? true : false}
                              />

                              {touched.aadhaar && errors.aadhaar ? (
                                <>
                                  <div className="error-msg">{errors.aadhaar}</div>
                                </>
                              ) : null}

                              {values.aadhaar.length === 12 && !errors.aadhaar && (
                                <div className={styles.check}>
                                  <img src="/images/right.svg" alt="" />
                                </div>
                              )}
                            </div>

                            {item.tag && <div className={styles.recommended}>{item.tag}</div>}

                            {isOTPVisible && (
                              <>
                                <div className="mt-1">
                                  <div className={`${styles.text} ${styles.bigText} ml-0`}>Enter OTP</div>
                                  <div className={styles.otpInputs}>
                                    <OtpInput
                                      value={otp}
                                      onChange={(value) => {
                                        setOtp(value);

                                        if (value.length === 6) {
                                          googleEvent_LOJ032();
                                        } else if (value.length === 0) {
                                          googleEvent_LOJ033();
                                        }
                                      }}
                                      numInputs={6}
                                      inputType="tel"
                                      renderSeparator={<span>&nbsp;</span>}
                                      renderInput={(props) => <input {...props} />}
                                    />
                                  </div>
                                </div>
                                <div>
                                  <p className={styles.notReceivedMsg}>Have not received your OTP ?</p>
                                  <p className={styles.notReceivedMsg}>
                                    <span
                                      className={styles.resend}
                                      onClick={() => {
                                        if (seconds === 0) {
                                          handleResendAadhaarOTP(values.aadhaar);
                                        }
                                      }}
                                    >
                                      Resend
                                    </span>{" "}
                                    {seconds > 0 && <span>in {convertSecondsToMinutes()} seconds</span>}
                                  </p>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>

                <ContactMessage />
              </div>

              <div className="vertical-column-bottom">
                <DataSafetyMessage />

                <CustomButton text="Continue" type="submit" />
              </div>
            </div>
          </form>
        )}
      </Formik>

      <AadhaarExistModal openAadhaarModal={openAadhaarModal} setOpenAadhaarModal={setOpenAadhaarModal} />
    </div>
  );
}
